/**
 * 프론트 버전 자동 체크 + 구버전이면 캐시 정리 후 자동 reload.
 *
 * <p>흐름:
 * <ol>
 *   <li>세션 시작 시 {@code GET /version.json} (Cache-Control: no-store)</li>
 *   <li>서버의 {@code builtAt} 이 번들된 {@code import.meta.env.APP_BUILT_AT} 보다 새로우면
 *       구버전 판정</li>
 *   <li>localStorage 의 재시도 카운터 확인 — 같은 서버 버전에 대해 3회 이상 실패하면 stop
 *       (무한 loop 방지)</li>
 *   <li>카운터 증가 → ServiceWorker unregister + CacheStorage 비움 → {@code location.reload()}</li>
 *   <li>다음 로드에서 번들이 서버와 일치하면 조용히 통과. 다른 더 새 버전이 또 나오면 별도
 *       카운터로 다시 3회 재시도 가능.</li>
 * </ol>
 *
 * <p>실패·네트워크 에러는 silent — 체크 때문에 앱이 크래시 나면 안 됨.
 */

const MAX_REFRESH_ATTEMPTS = 3;
const STORAGE_PREFIX = "rpg-web:refresh-attempts:";

interface ServerVersionInfo {
    version: string;
    builtAt: string;
}

/**
 * 앱 bootstrap 시 한 번 호출. 서버 버전과 번들된 버전을 비교해, 서버가 더 새면 자동 refresh.
 * 네트워크 실패 / JSON 파싱 실패 / 카운터 초과 는 모두 silent — 그냥 현 상태 유지.
 */
export async function checkAndMaybeRefreshIfStale(): Promise<void> {
    const bundledBuiltAt = (import.meta.env as { APP_BUILT_AT?: string }).APP_BUILT_AT;
    if (!bundledBuiltAt) {
        // 번들에 timestamp 자체가 없으면 (예: 개발 서버) skip.
        return;
    }

    let server: ServerVersionInfo | null = null;
    try {
        const response = await fetch("/version.json", { cache: "no-store" });
        if (!response.ok) return;
        const data = await response.json() as unknown;
        if (!isServerVersionInfo(data)) return;
        server = data;
    } catch {
        return;
    }

    // 서버 builtAt 이 bundled 보다 새롭지 않으면 최신. 카운터 정리 후 종료.
    if (server.builtAt <= bundledBuiltAt) {
        clearAllRefreshCounters();
        return;
    }

    // 같은 server 버전에 대해 이미 3회 이상 시도했다면 멈춤.
    const storageKey = `${STORAGE_PREFIX}${server.builtAt}`;
    const attempts = readCounter(storageKey);
    if (attempts >= MAX_REFRESH_ATTEMPTS) {
        console.warn(
            `[version-check] 서버는 ${server.builtAt} 이지만 현재 번들은 ${bundledBuiltAt}. ` +
            `${MAX_REFRESH_ATTEMPTS}회 재시도 후에도 적용되지 않아 자동 새로고침을 중지합니다. ` +
            `홈 화면의 "업데이트 받기" 버튼을 수동으로 눌러 주세요.`
        );
        return;
    }

    writeCounter(storageKey, attempts + 1);
    try {
        if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map(r => r.unregister()));
        }
    } catch {
        // SW 없거나 실패 — 무시하고 진행
    }
    try {
        if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map(k => caches.delete(k)));
        }
    } catch {
        // 캐시 API 없음 — 무시
    }
    window.location.reload();
}

function isServerVersionInfo(value: unknown): value is ServerVersionInfo {
    if (!value || typeof value !== "object") return false;
    const v = value as Record<string, unknown>;
    return typeof v.version === "string" && typeof v.builtAt === "string";
}

function readCounter(key: string): number {
    try {
        const raw = window.localStorage?.getItem(key);
        const n = raw ? parseInt(raw, 10) : 0;
        return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
        return 0;
    }
}

function writeCounter(key: string, value: number): void {
    try {
        window.localStorage?.setItem(key, String(value));
    } catch {
        // localStorage 비활성화된 환경 — 무시
    }
}

/**
 * 이전에 사용된 모든 refresh 카운터 제거. 버전이 일치하게 됐을 때 호출.
 * (다음 업데이트 때 카운터가 fresh 하게 3회부터 시작되도록.)
 */
function clearAllRefreshCounters(): void {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const k = window.localStorage.key(i);
            if (k && k.startsWith(STORAGE_PREFIX)) keysToRemove.push(k);
        }
        keysToRemove.forEach(k => window.localStorage.removeItem(k));
    } catch {
        // localStorage 비활성화 — 무시
    }
}
